"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CreateTrackingPageModal = void 0;
/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-unused-vars */
const react_1 = __importDefault(require("react"));
const client_1 = require("@apollo/client");
const react_hook_form_1 = require("react-hook-form");
const polaris_1 = require("@shopify/polaris");
const zod_1 = require("@hookform/resolvers/zod");
const zod_2 = require("zod");
const AccountContext_1 = require("contexts/AccountContext");
const styles_1 = require("./styles");
const graphql_1 = require("components/Dashboard/TrackingPages/graphql");
const DomainDropdown_1 = require("./DomainDropdown");
const defaultFieldValues = {
    title: 'Track Your Order',
    description: '',
    pageSlug: 'order-tracking',
    baseUrl: '',
    isDefault: true
};
const fieldLabels = {
    title: 'Page Title',
    description: 'Page Description',
    baseUrl: 'Domain',
    pageSlug: 'Page Suffix',
    isDefault: 'Set as default tracking page URL'
};
const schema = zod_2.z.object({
    title: zod_2.z
        .string({
        invalid_type_error: `${fieldLabels.title} must be a string`
    })
        .min(1, { message: `${fieldLabels.title} is a required field` }),
    description: zod_2.z
        .string({
        invalid_type_error: `${fieldLabels.description} must be a string`
    })
        .optional(),
    baseUrl: zod_2.z
        .string()
        .min(1, { message: `${fieldLabels.baseUrl} is a required field` })
        .regex(/^(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,6}$/, {
        message: `${fieldLabels.baseUrl} must have a valid format. e.g. example.com`
    }),
    pageSlug: zod_2.z
        .string()
        .min(1, { message: `${fieldLabels.pageSlug} is a required field` })
        // eslint-disable-next-line no-useless-escape
        .regex(/^[a-z0-9_\-]+$/, {
        message: `${fieldLabels.pageSlug} has invalid format`
    }),
    isDefault: zod_2.z.boolean({
        required_error: 'isDefault is required',
        invalid_type_error: 'isDefault must be a boolean'
    })
});
const CreateTrackingPageModal = ({ active, onChangeSuccessAlert, onChangeSuccessTitle, onClose }) => {
    var _a, _b;
    const { account, setAccount } = (0, AccountContext_1.useAccount)();
    const { control, watch, setError, formState: { isValid, errors }, handleSubmit, reset } = (0, react_hook_form_1.useForm)({
        mode: 'onChange',
        defaultValues: defaultFieldValues,
        resolver: (0, zod_1.zodResolver)(schema)
    });
    const [createTrackingPage, { loading }] = (0, client_1.useMutation)(graphql_1.CREATE_TRACKING_PAGE, {
        refetchQueries: [
            { query: graphql_1.GET_TRACKING_PAGES, variables: { accountId: account === null || account === void 0 ? void 0 : account.id } },
            'externalTrackingPage'
        ],
        onCompleted: ({ createExternalTrackingPage: { externalTrackingPage } }) => {
            if (account && externalTrackingPage.isDefault) {
                setAccount(Object.assign(Object.assign({}, account), { defaultTrackingPageUrl: externalTrackingPage.url }));
            }
            onChangeSuccessTitle(`'${externalTrackingPage.title}' has been created.`);
            onChangeSuccessAlert();
            reset(defaultFieldValues);
            onClose();
        },
        onError(error) {
            setError('root.serverError', {
                type: 'server',
                message: error.message
            });
        }
    });
    const baseUrl = watch('baseUrl');
    const pageSlug = watch('pageSlug');
    const onSubmit = handleSubmit((data) => {
        if (!account) {
            throw new Error('No account selected');
        }
        const transformedData = Object.assign(Object.assign({}, data), { baseUrl: `https://${data.baseUrl}` });
        createTrackingPage({
            variables: { input: Object.assign(Object.assign({}, transformedData), { accountId: account.id }) }
        });
    });
    return (react_1.default.createElement(polaris_1.Modal, { instant: true, onClose: onClose, open: active, primaryAction: {
            content: 'Create Tracking Page',
            loading,
            onAction: onSubmit,
            disabled: !isValid
        }, title: 'Create a Tracking Page' },
        react_1.default.createElement(polaris_1.Modal.Section, null,
            react_1.default.createElement("form", null,
                react_1.default.createElement(polaris_1.FormLayout, null,
                    react_1.default.createElement("p", null, "Name your page and set the tracking page URL"),
                    react_1.default.createElement(react_hook_form_1.Controller, { control: control, name: 'title', render: (_a) => {
                            var _b;
                            var _c = _a.field, { ref } = _c, rest = __rest(_c, ["ref"]);
                            return (react_1.default.createElement(polaris_1.TextField, Object.assign({}, rest, { autoComplete: 'off', error: (_b = errors.title) === null || _b === void 0 ? void 0 : _b.message, label: fieldLabels.title })));
                        } }),
                    react_1.default.createElement(react_hook_form_1.Controller, { control: control, name: 'description', render: (_a) => {
                            var _b;
                            var _c = _a.field, { ref } = _c, rest = __rest(_c, ["ref"]);
                            return (react_1.default.createElement(polaris_1.TextField, Object.assign({}, rest, { autoComplete: 'off', error: (_b = errors.description) === null || _b === void 0 ? void 0 : _b.message, label: `${fieldLabels.description} (optional)`, multiline: 4 })));
                        } }),
                    react_1.default.createElement(react_hook_form_1.Controller, { control: control, name: 'baseUrl', render: (_a) => {
                            var _b;
                            var _c = _a.field, { ref } = _c, rest = __rest(_c, ["ref"]);
                            return (react_1.default.createElement(DomainDropdown_1.DomainDropdown, Object.assign({}, rest, { error: (_b = errors.baseUrl) === null || _b === void 0 ? void 0 : _b.message, label: fieldLabels.baseUrl })));
                        } }),
                    react_1.default.createElement(react_hook_form_1.Controller, { control: control, name: 'pageSlug', render: (_a) => {
                            var _b;
                            var _c = _a.field, { ref } = _c, rest = __rest(_c, ["ref"]);
                            return (react_1.default.createElement(polaris_1.TextField, Object.assign({}, rest, { autoComplete: 'off', error: (_b = errors.pageSlug) === null || _b === void 0 ? void 0 : _b.message, label: fieldLabels.pageSlug })));
                        } }),
                    baseUrl && pageSlug && !errors.baseUrl && !errors.pageSlug && (react_1.default.createElement("div", null,
                        react_1.default.createElement("p", null, "URL Preview"),
                        react_1.default.createElement(styles_1.Preview, null, `https://${baseUrl}/pages/${pageSlug}`))),
                    react_1.default.createElement(react_hook_form_1.Controller, { control: control, name: 'isDefault', render: (_a) => {
                            var _b = _a.field, { ref, value } = _b, rest = __rest(_b, ["ref", "value"]);
                            return (react_1.default.createElement(polaris_1.Checkbox, Object.assign({}, rest, { checked: value, label: fieldLabels.isDefault })));
                        } }),
                    ((_a = errors.root) === null || _a === void 0 ? void 0 : _a.serverError) && (react_1.default.createElement(polaris_1.InlineError, { fieldID: 'serverError', message: (_b = errors.root.serverError.message) !== null && _b !== void 0 ? _b : 'Oops! Something went wrong.' })))))));
};
exports.CreateTrackingPageModal = CreateTrackingPageModal;
